import classnames from 'classnames';
import React, {useEffect, useRef} from 'react';
import {IonToolbar, IonFooter, IonButtons} from '@ionic/react';

import {useResizeObserver} from 'src/hooks/useResizeObserver';
import PlatformService from 'src/services/PlatformService';
import {useModalContext} from '.';

import FooterButton, {FooterButtonType} from './footer-button';

import './footer.scss';

interface Props {
  // Primary call to action button, solid color style
  primaryButton: {
    centered?: boolean;
    rightAligned?: boolean;
    newTabTooltip?: boolean;
  } & FooterButtonType;
  // Secondary action button, outline style
  secondaryButton?: {
    // Setting this to true will make the secondary button rightAligned,
    // placing it immediately to the right of the primary button
    rightAligned?: boolean;
  } & FooterButtonType;
  // Tertiary action (commonly delete or cancel), link/text style
  tertiaryButton?: FooterButtonType;
  className?: string;
}

const Footer = ({primaryButton, secondaryButton, tertiaryButton, className}: Props) => {
  const footerRef = useRef<HTMLDivElement>(null);
  const {height: footerHeight} = useResizeObserver(footerRef);
  const {setFooterHeight, bodyOverflow, forceNotIonic} = useModalContext();

  useEffect(() => {
    if (footerRef.current) {
      setFooterHeight(footerHeight);
    }
  }, [setFooterHeight, footerHeight]);

  if (!forceNotIonic && PlatformService.isNativeApp()) {
    return (
      <IonFooter className="shared-modal-footer-ionic">
        <div
          id="shared-modal-footer"
          ref={footerRef}
          className={classnames('shared-modal-footer-content-ionic', className)}
          data-testid="shared-modal-footer"
        >
          <IonToolbar className="shared-modal-footer-toolbar-ionic">
            <IonButtons slot="primary">
              {secondaryButton && (
                <span className="mr-8">
                  <FooterButton buttonType="secondary" {...secondaryButton} />
                </span>
              )}
              {primaryButton && <FooterButton buttonType="primary" {...primaryButton} />}
            </IonButtons>
            <IonButtons slot="secondary">
              {tertiaryButton && <FooterButton buttonType="tertiary" {...tertiaryButton} />}
            </IonButtons>
          </IonToolbar>
        </div>
      </IonFooter>
    );
  }

  return (
    <div
      id="shared-modal-footer"
      ref={footerRef}
      className={classnames('shared-modal-footer', className, {overflow: bodyOverflow})}
      data-testid="shared-modal-footer"
    >
      {primaryButton && <FooterButton buttonType="primary" {...primaryButton} />}
      {secondaryButton && <FooterButton buttonType="secondary" {...secondaryButton} />}
      {((!!primaryButton && !!secondaryButton && !!tertiaryButton) ||
        secondaryButton?.rightAligned) && <div className="shared-modal-footer-spacer" />}
      {tertiaryButton && <FooterButton buttonType="tertiary" {...tertiaryButton} />}
    </div>
  );
};

export default Footer;
